import Vue from 'vue';
import Component from 'vue-class-component';

interface DropdownData {
  options: Array<string | number | { text: string; value: any }>;
  highlightedIndex: number;
  selectedValue: any;
  position: { top: number; left: number; width: number };
  itemText: string | Function;
  itemValue: string | Function;
  selectCallback: (value: any) => void;
}

@Component({
  name: 'ed-select-custom-dropdown',
})
export default class EdSelectCustomDropdown extends Vue {
  public isVisible: boolean = false;
  private dropdownData: DropdownData = {
    options: [],
    highlightedIndex: -1,
    selectedValue: null,
    position: { top: 0, left: 0, width: 0 },
    itemText: 'text',
    itemValue: 'value',
    selectCallback: () => {}
  };

  get options(): Array<string | number | { text: string; value: any }> {
    return this.dropdownData.options;
  }

  get highlightedIndex(): number {
    return this.dropdownData.highlightedIndex;
  }

  get selectedValue(): any {
    return this.dropdownData.selectedValue;
  }

  get dropdownStyle(): object {
    return {
      position: 'fixed',
      top: `${this.dropdownData.position.top}px`,
      left: `${this.dropdownData.position.left}px`,
      width: `${this.dropdownData.position.width}px`,
      zIndex: 9999
    };
  }

  get itemText(): string | Function {
    return this.dropdownData.itemText;
  }

  get itemValue(): string | Function {
    return this.dropdownData.itemValue;
  }

  mounted() {
    this.$edEventBus.$on('show-select-dropdown', this.showDropdown);
    this.$edEventBus.$on('hide-select-dropdown', this.hideDropdown);
    this.$edEventBus.$on('update-dropdown-highlight', this.updateHighlight);
    this.$edEventBus.$on('update-dropdown-position', this.updatePosition);
  }

  beforeDestroy() {
    this.$edEventBus.$off('show-select-dropdown', this.showDropdown);
    this.$edEventBus.$off('hide-select-dropdown', this.hideDropdown);
    this.$edEventBus.$off('update-dropdown-highlight', this.updateHighlight);
    this.$edEventBus.$off('update-dropdown-position', this.updatePosition);
  }

  showDropdown(data: {
    options: Array<string | number | { text: string; value: any }>;
    selectedValue: any;
    position: { top: number; left: number; width: number };
    itemText: string | Function;
    itemValue: string | Function;
    selectCallback: (value: any) => void;
  }) {
    setTimeout(() => {
      this.dropdownData = {
        ...this.dropdownData,
        ...data,
      };
      this.isVisible = true;
      this.highlightSelected();
    }, 100);
  }

  hideDropdown() {
    this.isVisible = false;
  }

  updateHighlight(index: number) {
    this.dropdownData.highlightedIndex = index;
    this.$nextTick(() => {
      this.scrollToHighlighted();
    });
  }

  updatePosition(position: { top: number; left: number; width: number }) {
    this.dropdownData.position = position;
  }

  selectOption(option: string | number | { text: string; value: any }) {
    const value = this.getValue(option);
    this.dropdownData.selectCallback(value);
  }

  highlightSelected() {
    const selectedValue = this.selectedValue;
    const index = this.options.findIndex(
      option => this.getValue(option) === selectedValue
    );
    this.dropdownData.highlightedIndex = index >= 0 ? index : -1;
    
    this.$nextTick(() => {
      this.scrollToHighlighted();
    });
  }

  getDisplayText(option: any): string {
    if (typeof this.itemText === 'function') return this.itemText(option);
    return typeof option === 'object' ? option[this.itemText] : String(option);
  }

  getValue(option: any): any {
    if (typeof this.itemValue === 'function') return this.itemValue(option);
    return typeof option === 'object' ? option[this.itemValue] : option;
  }

  isOptionSelected(option: any): boolean {
    return this.getValue(option) === this.selectedValue;
  }

  scrollToHighlighted() {
    const dropdown = this.$refs.dropdown as HTMLElement;
    const highlighted = dropdown?.querySelector(
      '.ed-select-custom-dropdown__option--highlighted'
    ) as HTMLElement;

    if (highlighted) {
      highlighted.scrollIntoView({
        block: 'nearest',
      });
    }
  }
}
